import { Building2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';

/**
 * Clinic picker for patients registered at multiple Clinio clinics.
 */
export function PortalClinics() {
  const { t } = useTranslation();
  const { clinics, activeClinicId, setActiveClinicId, isLoading } = usePortalActiveClinic();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-gray-500">{t('app.loading')}</p>
      </div>
    );
  }

  if (clinics.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('portal.clinics.title')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('portal.clinics.empty')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('portal.clinics.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('portal.clinics.subtitle')}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {clinics.map((clinic) => {
          const isActive = clinic.clinic_id === activeClinicId;
          return (
            <button
              key={clinic.clinic_id}
              type="button"
              onClick={() => setActiveClinicId(clinic.clinic_id)}
              className={`flex items-center gap-4 rounded-lg border p-4 text-left transition-colors ${
                isActive
                  ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/20'
                  : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                {clinic.clinic_logo ? (
                  <img
                    src={clinic.clinic_logo}
                    alt=""
                    className="h-10 w-10 rounded object-contain"
                  />
                ) : (
                  <Building2 className="h-6 w-6 text-gray-500" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-gray-900 dark:text-white">
                  {clinic.clinic_name}
                </p>
                {isActive && (
                  <p className="mt-1 flex items-center text-xs text-indigo-600 dark:text-indigo-400">
                    <Check className="mr-1 h-3.5 w-3.5" />
                    {t('portal.clinics.active')}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
